import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { debtsApi } from '@/lib/api'
import { Debt, PayoffPlan } from '@/types'
import { formatCurrency, cn } from '@/lib/utils'
import DebtFormDialog from '@/components/DebtFormDialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { Plus, Pencil, Check, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react'

// ─── Helpers ────────────────────────────────────────────────────────────────

const DEBT_TYPE_LABELS: Record<string, string> = {
  credit_card: 'Credit Card',
  student_loan: 'Student Loan',
  auto_loan: 'Auto Loan',
  personal_loan: 'Personal Loan',
  mortgage: 'Mortgage',
  other: 'Other',
}

const DEBT_TYPE_BADGE: Record<string, string> = {
  credit_card: 'bg-red-100 text-red-800',
  mortgage: 'bg-gray-100 text-gray-800',
  student_loan: 'bg-blue-100 text-blue-800',
  auto_loan: 'bg-orange-100 text-orange-800',
  personal_loan: 'bg-purple-100 text-purple-800',
  other: 'bg-gray-100 text-gray-800',
}

// ─── DebtCard ────────────────────────────────────────────────────────────────

function DebtCard({
  debt,
  onEdit,
  onMarkPaidOff,
  isPaying,
}: {
  debt: Debt
  onEdit: (d: Debt) => void
  onMarkPaidOff: (d: Debt) => void
  isPaying: boolean
}) {
  const rawProgress =
    debt.original_balance > 0
      ? ((debt.original_balance - debt.current_balance) / debt.original_balance) * 100
      : 0
  const progress = Math.max(0, Math.min(100, rawProgress))

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-start justify-between">
        <div className="space-y-1">
          <CardTitle className="text-base">{debt.name}</CardTitle>
          <span
            className={cn(
              'inline-block text-xs font-medium px-2 py-0.5 rounded-full',
              DEBT_TYPE_BADGE[debt.debt_type] ?? 'bg-gray-100 text-gray-800'
            )}
          >
            {DEBT_TYPE_LABELS[debt.debt_type] ?? debt.debt_type}
          </span>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(debt)}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-green-600 hover:text-green-700"
            onClick={() => onMarkPaidOff(debt)}
            disabled={isPaying}
            title="Mark as paid off"
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline justify-between">
          <span className="text-lg font-bold">{formatCurrency(debt.current_balance)}</span>
          <span className="text-sm text-muted-foreground">{debt.interest_rate}% APR</span>
        </div>
        <div>
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-muted-foreground mt-1">
            {formatCurrency(debt.original_balance - debt.current_balance)} paid of{' '}
            {formatCurrency(debt.original_balance)}
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          Min. payment: {formatCurrency(debt.minimum_payment)}/mo
        </p>
      </CardContent>
    </Card>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DebtPage() {
  const queryClient = useQueryClient()

  // Dialog state
  const [formOpen, setFormOpen] = useState(false)
  const [editingDebt, setEditingDebt] = useState<Debt | undefined>(undefined)

  // Payoff calculator state
  const [extraPayment, setExtraPayment] = useState(0)
  const [method, setMethod] = useState<'snowball' | 'avalanche'>('snowball')

  // Paid-off section collapse
  const [paidOffExpanded, setPaidOffExpanded] = useState(false)

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: debts = [], isLoading } = useQuery<Debt[]>({
    queryKey: ['debts'],
    queryFn: async () => (await debtsApi.getAll()).data,
  })

  const activeDebts = debts.filter((d) => d.paid_off_at === null)
  const paidOffDebts = debts.filter((d) => d.paid_off_at !== null)

  const { data: plan, isLoading: planLoading } = useQuery<PayoffPlan>({
    queryKey: ['debts', 'payoff-plan', method, extraPayment],
    queryFn: async () => (await debtsApi.getPayoffPlan(method, extraPayment)).data,
    enabled: activeDebts.length > 0,
  })

  // ── Mutations ────────────────────────────────────────────────────────────

  const markPaidOffMutation = useMutation({
    mutationFn: (debt: Debt) =>
      debtsApi.update(debt.id, {
        paid_off_at: new Date().toISOString().split('T')[0],
        current_balance: 0,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debts'] })
    },
  })

  // ── Derived totals ───────────────────────────────────────────────────────

  const totalDebt = activeDebts.reduce((sum, d) => sum + d.current_balance, 0)
  const totalMinimums = activeDebts.reduce((sum, d) => sum + d.minimum_payment, 0)

  // ── Handlers ─────────────────────────────────────────────────────────────

  function openCreate() {
    setEditingDebt(undefined)
    setFormOpen(true)
  }

  function openEdit(debt: Debt) {
    setEditingDebt(debt)
    setFormOpen(true)
  }

  function handleFormSuccess() {
    queryClient.invalidateQueries({ queryKey: ['debts'] })
  }

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="container py-6 space-y-8 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingDown className="h-6 w-6" />
            Debt Payoff Tracker
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Track your debts and build a payoff plan
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-1" />
          Add Debt
        </Button>
      </div>

      {/* ── Debt List ─────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader><div className="h-5 w-2/3 bg-muted rounded" /></CardHeader>
              <CardContent className="space-y-2">
                <div className="h-4 w-full bg-muted rounded" />
                <div className="h-4 w-3/4 bg-muted rounded" />
                <div className="h-2 w-full bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : activeDebts.length === 0 && paidOffDebts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <TrendingDown className="h-12 w-12 mb-3 opacity-25" />
          <p className="font-medium">No active debts — great job! 🎉</p>
          <p className="text-sm mt-1">Add a debt to start tracking your payoff progress.</p>
        </div>
      ) : (
        <>
          {/* Summary card */}
          {activeDebts.length > 0 && (
            <Card>
              <CardContent className="pt-4 pb-3 flex flex-wrap gap-6">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Debt</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalDebt)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Monthly Minimums</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalMinimums)}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Active debts grid */}
          {activeDebts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeDebts.map((debt) => (
                <DebtCard
                  key={debt.id}
                  debt={debt}
                  onEdit={openEdit}
                  onMarkPaidOff={(d) => markPaidOffMutation.mutate(d)}
                  isPaying={markPaidOffMutation.isPending}
                />
              ))}
            </div>
          )}

          {/* Paid-off collapsible */}
          {paidOffDebts.length > 0 && (
            <div>
              <button
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-3"
                onClick={() => setPaidOffExpanded((v) => !v)}
              >
                {paidOffExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Paid Off ({paidOffDebts.length})
              </button>
              {paidOffExpanded && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
                  {paidOffDebts.map((debt) => (
                    <Card key={debt.id} className="border-dashed">
                      <CardContent className="pt-4 pb-3 space-y-1">
                        <p className="font-medium text-sm line-through">{debt.name}</p>
                        <span
                          className={cn(
                            'inline-block text-xs font-medium px-2 py-0.5 rounded-full',
                            DEBT_TYPE_BADGE[debt.debt_type] ?? 'bg-gray-100 text-gray-800'
                          )}
                        >
                          {DEBT_TYPE_LABELS[debt.debt_type] ?? debt.debt_type}
                        </span>
                        <p className="text-xs text-muted-foreground">
                          Paid off {debt.paid_off_at}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Payoff Calculator ─────────────────────────────────────────────── */}
      {activeDebts.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Payoff Calculator</h2>

          {/* Controls */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="extra-payment">Extra Monthly Payment</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input
                  id="extra-payment"
                  type="number"
                  min={0}
                  step="1"
                  className="pl-7 w-36"
                  value={extraPayment === 0 ? '' : extraPayment}
                  placeholder="0"
                  onChange={(e) => setExtraPayment(Number(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant={method === 'snowball' ? 'default' : 'outline'}
                  onClick={() => setMethod('snowball')}
                >
                  Snowball
                </Button>
                <Button
                  size="sm"
                  variant={method === 'avalanche' ? 'default' : 'outline'}
                  onClick={() => setMethod('avalanche')}
                >
                  Avalanche
                </Button>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            {method === 'snowball' ? (
              <>
                <span className="font-medium text-foreground">Snowball:</span> pay
                the smallest balance first while making minimums on the rest. Each
                time a debt is paid off, its minimum rolls into the next smallest —
                the "snowball" grows. Best for motivation (quick wins), usually
                costs slightly more in total interest than Avalanche.
              </>
            ) : (
              <>
                <span className="font-medium text-foreground">Avalanche:</span> pay
                the highest interest rate first while making minimums on the rest.
                Freed-up minimums roll into the next highest rate as debts are
                paid off. Mathematically optimal — minimizes total interest and
                usually the fastest payoff.
              </>
            )}
          </p>

          {/* Plan summary & chart */}
          {planLoading ? (
            <p className="text-muted-foreground text-sm">Loading payoff plan…</p>
          ) : plan ? (
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-4 pb-3">
                  <p className="text-sm text-muted-foreground">
                    Debt-free in{' '}
                    <span className="font-semibold text-foreground">{plan.total_months} months</span>{' '}
                    ({plan.payoff_date ?? '—'}) · Total interest:{' '}
                    <span className="font-semibold text-foreground">
                      {formatCurrency(plan.total_interest)}
                    </span>
                  </p>
                </CardContent>
              </Card>

              {/* Balance chart */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Balance Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={plan.monthly_schedule}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="month"
                        tickFormatter={(m) => `Mo ${m}`}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip
                        formatter={(value: number) => [formatCurrency(value), 'Balance']}
                        labelFormatter={(label) => `Month ${label}`}
                      />
                      <Area
                        type="monotone"
                        dataKey="total_balance"
                        stroke="#3b82f6"
                        fill="#3b82f680"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Per-debt table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Payoff Timeline by Debt</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b">
                          <th className="pb-2 pr-2 font-medium w-6">#</th>
                          <th className="pb-2 pr-4 font-medium">Name</th>
                          <th className="pb-2 pr-4 font-medium text-right">Balance</th>
                          <th className="pb-2 pr-4 font-medium text-right">Rate</th>
                          <th className="pb-2 pr-4 font-medium text-right">Min. Payment</th>
                          <th className="pb-2 pr-4 font-medium text-right">Months</th>
                          <th className="pb-2 pr-4 font-medium text-right">Total Interest</th>
                          <th className="pb-2 font-medium text-right">Payoff Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...plan.debts]
                          .sort((a, b) =>
                            method === 'avalanche'
                              ? b.interest_rate - a.interest_rate
                              : a.current_balance - b.current_balance
                          )
                          .map((item, idx) => (
                          <tr key={item.id} className="border-b last:border-0">
                            <td className="py-2 pr-2 text-right text-muted-foreground tabular-nums w-6">{idx + 1}.</td>
                            <td className="py-2 pr-4">{item.name}</td>
                            <td className="py-2 pr-4 text-right">{formatCurrency(item.current_balance)}</td>
                            <td className="py-2 pr-4 text-right">{item.interest_rate}%</td>
                            <td className="py-2 pr-4 text-right">{formatCurrency(item.minimum_payment)}</td>
                            <td className="py-2 pr-4 text-right">{item.months_to_payoff ?? '—'}</td>
                            <td className="py-2 pr-4 text-right">{formatCurrency(item.total_interest)}</td>
                            <td className="py-2 text-right">{item.payoff_date ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </div>
      )}

      {/* Form dialog */}
      <DebtFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        debt={editingDebt}
        onSuccess={handleFormSuccess}
      />
    </div>
  )
}
